import { useEffect, useRef, useState } from "react";

import { Typography } from "@mui/material";

import { daysOfWeek } from "../../../../../constants";
import { formatHourMinute } from "../../../../../utils";
import { GeneralAvailabilityPerDay } from "./GeneralAvailabilityPerDay";
import {
	TAvailableHour,
	useTeachingPolicy,
} from "../../../../../hooks/useTeachingPolicy";

export function GeneralAvailability() {
	const { availableHours: savedAvailableHours, saveGeneralAvailability } =
		useTeachingPolicy();

	const [availableHours, setAvailableHours] = useState<TAvailableHour[]>(
		savedAvailableHours.map((availableHour) => ({
			dayOfWeek: availableHour.dayOfWeek,
			startHour: availableHour.startHour,
			startMinute: availableHour.startMinute,
			endHour: availableHour.endHour,
			endMinute: availableHour.endMinute,
		})),
	);
	const saveGeneralAvailabilityTimeout = useRef<NodeJS.Timeout | null>(null);
	const tempAvailabilityHours = useRef<TAvailableHour[]>([]);

	useEffect(() => {
		setAvailableHours(
			savedAvailableHours.map((availableHour) => ({
				dayOfWeek: availableHour.dayOfWeek,
				startHour: availableHour.startHour,
				startMinute: availableHour.startMinute,
				endHour: availableHour.endHour,
				endMinute: availableHour.endMinute,
			})),
		);
	}, [savedAvailableHours]);

	const handleChangeAvailableHours = (
		daysOfWeek: number[],
		newAvailableHoursOfDay: TAvailableHour[],
	) => {
		const availableHoursOfOtherDays = availableHours.filter(
			(availableHour) => !daysOfWeek.includes(availableHour.dayOfWeek),
		);

		tempAvailabilityHours.current = [
			...availableHoursOfOtherDays,
			...newAvailableHoursOfDay,
		];

		setAvailableHours([
			...availableHoursOfOtherDays,
			...newAvailableHoursOfDay,
		]);

		if (saveGeneralAvailabilityTimeout.current) {
			clearTimeout(saveGeneralAvailabilityTimeout.current);
			saveGeneralAvailabilityTimeout.current = null;
		}

		saveGeneralAvailabilityTimeout.current = setTimeout(() => {
			saveGeneralAvailability(tempAvailabilityHours.current);
		}, 1000);
	};

	return (
		<div className="section-separator">
			<Typography className="small-title-text bold">
				General availability
			</Typography>
			<Typography className="normal-text item-separator-tiny">
				When do you want to teach?
			</Typography>
			{availableHours &&
				daysOfWeek.map((dayOfWeek) => {
					const availableHoursOfDay = availableHours
						.filter(
							(availableHour) => availableHour.dayOfWeek === dayOfWeek.value,
						)
						.sort((a, b) => {
							if (
								formatHourMinute(a.startHour, a.startMinute) >
								formatHourMinute(b.startHour, b.startMinute)
							) {
								return 1;
							} else {
								return -1;
							}
						});
					const key = `${dayOfWeek.value}|${availableHoursOfDay
						.map(
							(availableHour) =>
								`${availableHour.startHour}-${availableHour.startMinute}-${availableHour.endHour}-${availableHour.endMinute}`,
						)
						.join("|")}`;
					return (
						<GeneralAvailabilityPerDay
							key={key}
							dayOfWeek={dayOfWeek}
							availableHours={availableHoursOfDay}
							handleChangeAvailableHours={handleChangeAvailableHours}
						/>
					);
				})}
		</div>
	);
}
